import {
  Element,
  InstallSpec,
  ItemComponent,
  Option,
} from "@p0security/shared/install/types";

// Based on allowable field names in Firestore
const VALID_COMPONENT_NAME_PATTERN = /^[A-Za-z][A-Za-z0-9-]*$/;

const postgresLegacyComponentNameValidator = async (
  _id: string,
  field: string
) => {
  if (!field.match(VALID_COMPONENT_NAME_PATTERN)) {
    return "Identifier must start with a letter and contain only alphanumeric characters and hyphens";
  }
};

const cloudSqlInstanceIdValidator = async (_id: string, field: string) => {
  if (field.match(/:/)) {
    return "CloudSQL instance ID must not contain colons. Enter only the instance ID, not the full connection name.";
  }
};

const postgresLegacyInstallTypeOptions = {
  "cloud-sql": Option({
    maturity: "ga" as const,
    label: "Google CloudSQL",
    schema: {
      projectId: Element({
        label: "GCP project ID",
        type: "dynamic",
        step: "new",
      }),
      region: Element({
        label: "GCP region",
        description: "The region where the CloudSQL instance is located.",
        type: "string",
        step: "new",
      }),
      instanceId: Element({
        label: "CloudSQL instance ID",
        description:
          "The ID of the CloudSQL instance hosting the database. It must have public IP access enabled.",
        type: "string",
        step: "new",
        validator: cloudSqlInstanceIdValidator,
      }),
    },
  }),
  rds: Option({
    maturity: "ga" as const,
    label: "AWS RDS",
    schema: {
      account: Element({
        label: "AWS account ID",
        type: "dynamic",
        step: "new",
      }),
      region: Element({
        label: "AWS region",
        description: "The region where the RDS instance is located.",
        type: "string",
        step: "new",
      }),
      resourceId: Element({
        label: "Resource ID",
        description:
          "The resource ID of the RDS instance. This will likely start with db-",
        type: "string",
        step: "new",
      }),
      instance: Element({
        label: "Instance ID",
        description:
          "The ID of the RDS instance hosting the database. This is typically the identifier provided by AWS.",
        type: "string",
        step: "new",
      }),
      hostname: Element({
        label: "Hostname",
        description:
          "The public hostname of the RDS instance. This is typically the endpoint provided by AWS.",
        type: "string",
        step: "new",
      }),
      port: Element({
        label: "Port",
        description: "The port on which the PostgreSQL instance is listening.",
        type: "string",
        default: 5432,
        step: "new",
      }),
      connectivity: Element({
        label: "Connectivity",
        description: "How P0 will connect to the RDS database.",
        type: "select" as const,
        step: "new",
        default: "public" as const,
        options: {
          public: Option({
            maturity: "ga" as const,
            label: "Public endpoint",
            description:
              "Use the public endpoint to connect to the RDS instance.",
            schema: {},
          }),
          private: Option({
            maturity: "ga" as const,
            label: "Private endpoint",
            description: "Install a Security Perimeter into your AWS account.",
            schema: {
              securityPerimeter: Element({
                label: "Security perimeter",
                type: "dynamic",
                step: "new",
              }),
              securityGroupId: Element({
                label: "Security group ID",
                description:
                  "An AWS Security Group assigned to the RDS instance.",
                type: "string",
                step: "new",
              }),
            },
          }),
        },
      }),
    },
  }),
};

export const PostgresLegacyComponents = InstallSpec({
  "access-management": ItemComponent({
    label: "Access management",
    description: "Manage access to a PostgreSQL database.",
    type: "string",
    maturity: "ga",
    schema: {
      installType: Element({
        label: "Installation type",
        description: "How is the PostgreSQL database hosted or managed?",
        type: "select" as const,
        default: "cloud-sql" as const,
        options: postgresLegacyInstallTypeOptions,
        step: "new",
      }),
      databaseName: Element({
        label: "Database name",
        description:
          "The name of the database to manage. Have more than one on the same instance? Create an additional integration for each.",
        type: "string",
        step: "new",
      }),
    },
    validator: postgresLegacyComponentNameValidator,
  }),
});
